import { useEffect } from "react";

const SITE_NAME = "Sullivan Care";
const SITE_URL = "https://sullivancare.com";

interface SeoProps {
  title: string;
  description: string;
  /** Comma-separated keywords for meta keywords tag */
  keywords?: string;
  /** Canonical URL path, e.g. "/about". Defaults to current pathname. */
  canonical?: string;
  /** Set true to add noindex,nofollow for demo/internal pages */
  noIndex?: boolean;
}

function setMeta(attr: string, value: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr.startsWith("property") ? "property" : "name", value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export function useSeo({ title, description, keywords, canonical, noIndex }: SeoProps) {
  useEffect(() => {
    document.title = title;

    // Basic meta
    setMeta("name", "description", description);

    // Keywords
    if (keywords) {
      setMeta("name", "keywords", keywords);
    }

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "en_US");

    const fullUrl = `${SITE_URL}${canonical ?? window.location.pathname}`;
    setMeta("property", "og:url", fullUrl);

    // Twitter
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    // Canonical
    setLink("canonical", fullUrl);

    // Robots noindex
    const robotsMeta = document.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (!robotsMeta) {
        const m = document.createElement("meta");
        m.setAttribute("name", "robots");
        m.setAttribute("content", "noindex, nofollow");
        document.head.appendChild(m);
      } else {
        robotsMeta.setAttribute("content", "noindex, nofollow");
      }
    } else if (robotsMeta) {
      robotsMeta.setAttribute("content", "index, follow");
    }
  }, [title, description, keywords, canonical, noIndex]);
}
